// Learning Center reader (dealer portal) — a single training at /learning-center/:key.
// Resolves the live version for the key and scopes it to the current rooftop, so a link
// to a module the rooftop can't see (or that's no longer live) shows a friendly message.

import React from 'react';
import { Link as RouterLink, useParams } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { Box, Breadcrumbs, Button, CircularProgress, Link, Stack, Typography } from '@mui/material';
import { BiBook } from 'react-icons/bi';
import DefaultErrorFallback from '@components/Fallbacks/DefaultErrorFallback';
import { useLearningModule } from '../hooks/useLearningModule';
import ModuleReader from '../components/ModuleReader';

const LIBRARY_ROUTE = '/learning-center';

const LearningCenterModuleContent: React.FC = () => {
  const { key } = useParams<{ key: string }>();
  // Fetches the live version's markdown content and confirms it's in-scope for this rooftop.
  const { module, loading, error, refetch } = useLearningModule(key);

  return (
    <Box sx={{ p: 3, maxWidth: 900, mx: 'auto' }}>
      <Breadcrumbs separator="›" sx={{ mb: 2 }}>
        <Link component={RouterLink} to={LIBRARY_ROUTE} underline="hover" color="inherit">Learning Center</Link>
        <Typography color="text.primary">{module?.name ?? 'Training'}</Typography>
      </Breadcrumbs>

      {module ? (
        <ModuleReader module={module} />
      ) : loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>
      ) : error ? (
        // A failed request is not "this training doesn't exist" — offer a retry instead.
        <Box sx={{ textAlign: 'center', py: 8, color: 'text.secondary' }}>
          <Box sx={{ mb: 1, display: 'flex', justifyContent: 'center' }}><BiBook size={48} /></Box>
          <Typography sx={{ mb: 2 }}>Couldn’t load this training right now.</Typography>
          <Stack direction="row" spacing={1.5} justifyContent="center">
            <Button variant="contained" onClick={() => refetch()}>Try again</Button>
            <Button variant="outlined" component={RouterLink} to={LIBRARY_ROUTE}>Back to Learning Center</Button>
          </Stack>
        </Box>
      ) : (
        <Box sx={{ textAlign: 'center', py: 8, color: 'text.secondary' }}>
          <Box sx={{ mb: 1, display: 'flex', justifyContent: 'center' }}><BiBook size={48} /></Box>
          <Typography sx={{ mb: 2 }}>This training isn’t available.</Typography>
          <Button variant="outlined" component={RouterLink} to={LIBRARY_ROUTE}>Back to Learning Center</Button>
        </Box>
      )}
    </Box>
  );
};

const LearningCenterModulePage: React.FC = () => (
  <Sentry.ErrorBoundary
    fallback={({ error, resetError }) => (
      <DefaultErrorFallback message='Unexpected error caught at LearningCenterModulePage.tsx' error={error as Error} resetError={resetError} />
    )}
  >
    <LearningCenterModuleContent />
  </Sentry.ErrorBoundary>
);

export default LearningCenterModulePage;
